// Shared types for 1130 Studio CRM

// User types
export interface User {
  id: string
  email: string
  username: string
  role: 'admin'
  telegramId?: number
  telegramUsername?: string
  createdAt: Date
  updatedAt: Date
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
}

export interface JWTPayload {
  sub: string
  username: string
  role: string
  iat: number
  exp: number
}

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// Lead statuses
export const LEAD_STATUSES = [
  'new',
  'bd_assigned',
  'call_scheduled',
  'call_done',
  'qualified',
  'estimation',
  'converted',
  'lost',
] as const

export type LeadStatus = typeof LEAD_STATUSES[number]

// Lead sources
export const LEAD_SOURCES = ['website', 'telegram', 'manual', 'referral'] as const
export type LeadSource = typeof LEAD_SOURCES[number]

// Lost reasons
export const LOST_REASONS = [
  'price',
  'timing',
  'competitor',
  'no_response',
  'not_qualified',
  'spam',
  'postponed',
  'other',
] as const

export type LostReason = typeof LOST_REASONS[number]

// Project priorities
export const PROJECT_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const
export type ProjectPriority = typeof PROJECT_PRIORITIES[number]

// Payment statuses
export const PAYMENT_STATUSES = ['pending', 'paid'] as const
export type PaymentStatus = typeof PAYMENT_STATUSES[number]

// Payment types
export const PAYMENT_TYPES = ['prepayment', 'milestone', 'final'] as const
export type PaymentType = typeof PAYMENT_TYPES[number]

// Document types
export const DOCUMENT_TYPES = ['contract', 'invoice', 'act', 'specification', 'other'] as const
export type DocumentType = typeof DOCUMENT_TYPES[number]

// Document statuses
export const DOCUMENT_STATUSES = ['draft', 'review', 'sent', 'signed', 'archived'] as const
export type DocumentStatus = typeof DOCUMENT_STATUSES[number]

// Activity types
export const ACTIVITY_TYPES = ['status_change', 'note', 'call', 'email', 'document', 'created', 'updated', 'assigned'] as const
export type ActivityType = typeof ACTIVITY_TYPES[number]

// Entity types for activities
export const ENTITY_TYPES = ['lead', 'client', 'project'] as const
export type EntityType = typeof ENTITY_TYPES[number]

// Client types
export const CLIENT_TYPES = ['individual', 'company'] as const
export type ClientType = typeof CLIENT_TYPES[number]

// Lead interface
export interface Lead {
  id: string
  name: string
  email?: string
  phone?: string
  telegram?: string
  message?: string
  source: LeadSource
  status: LeadStatus
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  assignedTo?: string
  assignedUser?: User
  specification?: string
  preferredContact?: string[]
  lostReason?: LostReason
  lostComment?: string
  stageId?: string
  stage?: ProjectStage
  position: number
  createdAt: Date
  updatedAt: Date
}

// Client interface
export interface Client {
  id: string
  type: ClientType
  companyName?: string
  inn?: string
  ogrn?: string
  contactName: string
  email?: string
  phone?: string
  telegram?: string
  whatsapp?: string
  legalAddress?: string
  notes?: string
  leadId?: string
  createdAt: Date
  updatedAt: Date
}

// Activity interface
export interface Activity {
  id: string
  entityType: EntityType
  entityId: string
  userId?: string
  user?: User
  type: ActivityType
  content?: string
  metadata?: Record<string, unknown>
  createdAt: Date
}

// Stage types
export const STAGE_TYPES = ['lead', 'project', 'archive'] as const
export type StageType = typeof STAGE_TYPES[number]

// Subscription statuses
export const SUBSCRIPTION_STATUSES = ['pending', 'approved', 'rejected'] as const
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number]

// Project Stage interface
export interface ProjectStage {
  id: string
  name: string
  slug: string
  color: string
  position: number
  type: StageType
  isFinal: boolean
  createdAt: Date
}

// Project interface
export interface Project {
  id: string
  name: string
  description?: string
  clientId?: string
  client?: Client
  leadId?: string
  lead?: Lead
  stageId?: string
  stage?: ProjectStage
  position: number
  priority: ProjectPriority
  budget?: string
  currency: string
  startDate?: Date
  deadline?: Date
  createdAt: Date
  updatedAt: Date
  payments?: Payment[]
}

// Payment interface
export interface Payment {
  id: string
  projectId: string
  name: string
  amount: string
  status: PaymentStatus
  type?: PaymentType
  dueDate?: Date
  paidAt?: Date
  createdAt: Date
  updatedAt: Date
}

// Kanban data structure
export interface KanbanData {
  stages: ProjectStage[]
  projects: Record<string, Project[]>
}

// Pipeline card (unified lead + project)
export interface PipelineCard {
  id: string
  type: 'lead' | 'project'
  name: string
  position: number
  stageId: string
  createdAt: Date
  updatedAt: Date
  // Lead-specific fields
  email?: string | null
  phone?: string | null
  telegram?: string | null
  message?: string | null
  source?: LeadSource | null
  assignedTo?: string | null
  assignedUser?: Pick<User, 'id' | 'username' | 'email'> | null
  // Project-specific fields
  clientId?: string | null
  client?: Pick<Client, 'id' | 'companyName' | 'contactName' | 'email' | 'phone' | 'telegram'> | null
  priority?: ProjectPriority | null
  budget?: string | null
  deadline?: Date | null
  description?: string | null
}

// Pipeline data structure
export interface PipelineData {
  stages: ProjectStage[]
  cards: Record<string, PipelineCard[]>
}

// Lead Comment interface
export interface LeadComment {
  id: string
  leadId: string
  userId?: string
  user?: Pick<User, 'id' | 'username'>
  content: string
  createdAt: Date
}

// Meeting statuses
export const MEETING_STATUSES = ['scheduled', 'ongoing', 'completed', 'transcribed', 'cancelled'] as const
export type MeetingStatus = typeof MEETING_STATUSES[number]

// Meeting interface
export interface Meeting {
  id: string
  leadId?: string
  hostId?: string
  title: string
  description?: string
  scheduledAt: Date
  durationMinutes: number
  meetingUrl?: string
  status: MeetingStatus
  transcript?: string
  transcriptSummary?: string
  transcriptActionItems?: string[]
  transcribedAt?: Date
  startedAt?: Date
  endedAt?: Date
  actualDuration?: number
  lead?: Pick<Lead, 'id' | 'name' | 'email' | 'phone'>
  host?: Pick<User, 'id' | 'username' | 'email'>
  createdAt: Date
  updatedAt: Date
}

// Specification Version interface
export interface SpecificationVersion {
  id: string
  leadId: string
  version: number
  content: string
  changeSummary?: string
  createdBy?: string
  creator?: Pick<User, 'id' | 'username'>
  isAiGenerated: boolean
  createdAt: Date
}

// Document interface
export interface Document {
  id: string
  name: string
  type: DocumentType
  status: DocumentStatus
  filename: string
  storedFilename: string
  mimeType: string
  size: number
  description?: string
  projectId?: string
  project?: Project
  uploadedBy?: string
  uploader?: Pick<User, 'id' | 'username' | 'email'>
  createdAt: Date
  updatedAt: Date
}
